import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { DotLottieReact as _DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { DotLottieReactProps } from "@lottiefiles/dotlottie-react";
import { cn } from "../../lib/utils";
import drugstoreLottie from "../../lib/assets/drugstore.lottie";

// Wrapper that fixes @types/react 18.0.x incompatibility (ReactNode vs ReactElement).
function DotLottiePlayer(props: DotLottieReactProps): React.ReactElement | null {
  return _DotLottieReact(props) as React.ReactElement | null;
}

export interface HeroProps {
  className?: string;
}

/** Bloque hero con títulos rotativos y animación lottie. */
function Hero({ className = "" }: HeroProps) {
  const [titleNumber, setTitleNumber] = useState(0);
  const titles = useMemo(
    () => ["Celmar", "Ranking", "fácil", "transparente", "cercano"],
    []
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setTitleNumber((n) => (n === titles.length - 1 ? 0 : n + 1));
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles.length]);

  return (
    <div className={cn("w-full", className)}>
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-10 py-12 md:flex-row md:py-20 lg:py-24">
          {/* Text side */}
          <div className="flex flex-1 flex-col gap-4 text-center md:text-left">
            <h1 className="text-4xl font-normal tracking-tight md:text-6xl">
              <span className="text-[#205f43]">Bienvenido a algo</span>
              <span className="relative flex w-full justify-center overflow-hidden md:justify-start md:min-h-[1.1em] md:pb-2 md:pt-1">
                <span className="invisible" aria-hidden>
                  &nbsp;
                </span>
                {titles.map((title, index) => (
                  <motion.span
                    key={title + String(index)}
                    className="absolute left-0 right-0 font-semibold text-neutral-900 md:text-left"
                    initial={{ opacity: 0, y: -32 }}
                    transition={{ type: "spring", stiffness: 50, damping: 20 }}
                    animate={
                      titleNumber === index
                        ? { y: 0, opacity: 1 }
                        : {
                            y: titleNumber > index ? -48 : 48,
                            opacity: 0,
                          }
                    }
                  >
                    {title}
                  </motion.span>
                ))}
              </span>
            </h1>
          </div>

          {/* Lottie side */}
          <div className="flex flex-1 items-center justify-center">
            <DotLottiePlayer
              src={drugstoreLottie}
              loop
              autoplay
              className="w-full max-w-xs sm:max-w-sm md:max-w-md"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export { Hero };
